import {
    type Alignment,
    type Config,
    type DriveStep,
    type Driver,
    type PopoverDOM,
    type Side,
    driver,
} from 'driver.js';
import 'driver.js/dist/driver.css';
import './tours.css';
import { PILL_CLASSES } from '_shared_/UI/Components/buttons/pillClasses';

export type PopoverSide = Side;
export type PopoverAlign = Alignment;

export type EngineStep = {
    element?: string;
    title: string;
    text: string;
    interactive: boolean;
    disableInteraction?: boolean;
    /** Where the popover sits relative to the element; driver picks a side that fits when unset. */
    side?: PopoverSide;
    align?: PopoverAlign;
};

export type EngineLabels = {
    next: string;
    done: string;
    skip: string;
    close: string;
    progress: string;
};

export type EngineHooks = {
    onStepShown(index: number): void;
    /** Next on a narrated step: the runner, not driver, picks the step that follows. */
    onNext(index: number): void;
    onSkip(index: number): void;
    onDone(): void;
    onCloseRequested(): void;
    /** Where the step stands among the ones the tour shows: driver would count the ones it passes over too. */
    progressFor(index: number): { current: number; total: number };
};

export type TourEngine = {
    start(steps: EngineStep[], hooks: EngineHooks, labels: EngineLabels, from?: number): void;
    moveTo(index: number): void;
    refresh(): void;
    destroy(): void;
    isActive(): boolean;
};

const INTERACTIVE_CLASS = 'volpe-tour-interactive';

// How long a step waits for its anchor before it shows centred: a panel or a
// hint the step's own `enter` opens mounts within a frame or two.
const ANCHOR_WAIT_MS = 2000;

const noop = () => {};

/** driver's own template, filled with the runner's count. */
function progressText(template: string, { current, total }: { current: number; total: number }): string {
    return template.replace('{{current}}', String(current)).replace('{{total}}', String(total));
}

// The popover's buttons are the panel's footer pills. driver's own button
// class goes: its stylesheet loads after the app's and would repaint them on
// hover. driver finds its buttons by their role classes, not by this one.
function skipButton(label: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `volpe-tour-skip ${PILL_CLASSES.secondary}`;
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
}

function dressNextButton(button: HTMLButtonElement): void {
    button.className = `driver-popover-next-btn ${PILL_CLASSES.primary}`;
}

function toDriveStep(step: EngineStep, index: number, hooks: EngineHooks, labels: EngineLabels): DriveStep {
    const interactivePopover = step.interactive
        ? {
              showButtons: ['close' as const],
              // Arrow keys and the (hidden) next button reach these hooks; the
              // store, not the operator's keyboard, moves an interactive step.
              onNextClick: noop,
              onDoneClick: noop,
          }
        : { onNextClick: () => hooks.onNext(index) };

    return {
        element: step.element,
        disableActiveInteraction: step.disableInteraction ?? false,
        onHighlightStarted: () => {
            document.body.classList.toggle(INTERACTIVE_CLASS, step.interactive);
            // driver's `.driver-active *` rule would freeze the page this step
            // asks the operator to use.
            document.body.classList.toggle('driver-active', !step.interactive);
        },
        onHighlighted: () => hooks.onStepShown(index),
        popover: {
            title: step.title,
            description: step.text,
            ...(step.side ? { side: step.side } : {}),
            ...(step.align ? { align: step.align } : {}),
            ...interactivePopover,
            // driver hard-codes an English aria-label on the close button.
            onPopoverRender: (popover: PopoverDOM) => {
                popover.closeButton.setAttribute('aria-label', labels.close);
                popover.progress.textContent = progressText(labels.progress, hooks.progressFor(index));
                dressNextButton(popover.nextButton);
                if (step.interactive) {
                    popover.footerButtons.appendChild(skipButton(labels.skip, () => hooks.onSkip(index)));
                }
            },
        },
    };
}

export function createTourEngine(): TourEngine {
    let instance: Driver | null = null;

    const destroy = () => {
        document.body.classList.remove(INTERACTIVE_CLASS);
        instance?.destroy();
        instance = null;
    };

    return {
        start(steps, hooks, labels, from = 0) {
            destroy();
            const config: Config = {
                steps: steps.map((step, index) => toDriveStep(step, index, hooks, labels)),
                animate: true,
                allowClose: true,
                // A click on the dimmed area is a slip, not an answer.
                overlayClickBehavior: noop,
                // driver's black at the opacity of the reader's modal backdrop:
                // the tour dims the page the way the reader's own dialogs do.
                overlayOpacity: 0.75,
                stagePadding: 6,
                stageRadius: 8,
                waitForElement: ANCHOR_WAIT_MS,
                popoverClass: 'volpe-tour',
                showProgress: true,
                progressText: labels.progress,
                // A step's enter changes the reader's state (opens the piece,
                // flips its status), so going back would replay it: Previous is
                // hidden and ArrowLeft goes nowhere.
                showButtons: ['next', 'close'],
                onPrevClick: noop,
                nextBtnText: labels.next,
                doneBtnText: labels.done,
                onDoneClick: () => hooks.onDone(),
                // With this hook set driver defers the teardown to us, so the
                // runner can discard its draft before the overlay goes.
                onDestroyStarted: () => hooks.onCloseRequested(),
            };
            instance = driver(config);
            instance.drive(from);
        },
        moveTo(index) {
            instance?.moveTo(index);
        },
        refresh() {
            instance?.refresh();
        },
        destroy,
        isActive() {
            return instance?.isActive() ?? false;
        },
    };
}
